import { Center, Flex, ModalBody, ModalFooter, Stack, Tag } from '@chakra-ui/react'
import { toAssetId } from '@shapeshiftoss/caip'
import { NetworkTypes } from '@shapeshiftoss/types'
import { DefiParams, DefiQueryParams } from 'features/defi/contexts/DefiManagerProvider/DefiCommon'
import { matchPath } from 'react-router'
import { Amount } from 'components/Amount/Amount'
import { AssetIcon } from 'components/AssetIcon'
import { CircularProgress } from 'components/CircularProgress/CircularProgress'
import { Text } from 'components/Text'
import { useBrowserRouter } from 'hooks/useBrowserRouter/useBrowserRouter'
import { bnOrZero } from 'lib/bignumber/bignumber'
import { useFoxyBalances } from 'pages/Defi/hooks/useFoxyBalances'
import { selectAssetById } from 'state/slices/selectors'
import { useAppSelector } from 'state/store'

import { FoxyEmpty } from './FoxyEmpty'
import { WithdrawCard } from './WithdrawCard'

export const FoxyDetails = () => {
  const { opportunities, loading } = useFoxyBalances()
  const {
    query,
    history: browserHistory,
    location: browserLocation,
  } = useBrowserRouter<DefiQueryParams, DefiParams>()
  const match = matchPath<DefiParams>(browserLocation.pathname, {
    path: '/defi/:earnType/:provider/:action',
    exact: true,
  })
  const { chain, contractAddress, tokenId, rewardId } = query
  const opportunity = opportunities.find(e => e.contractAddress === contractAddress)
  const rewardBalance = bnOrZero(opportunity?.withdrawInfo.amount)
  const foxyBalance = bnOrZero(opportunity?.balance)
  const network = NetworkTypes.MAINNET
  const assetNamespace = 'erc20'
  const stakingAssetId = toAssetId({
    chain,
    network,
    assetNamespace,
    assetReference: tokenId,
  })
  const stakingAsset = useAppSelector(state => selectAssetById(state, stakingAssetId))
  const rewardAssetId = toAssetId({
    chain,
    network,
    assetNamespace,
    assetReference: rewardId,
  })
  const rewardAsset = useAppSelector(state => selectAssetById(state, rewardAssetId))
  const apy = bnOrZero(opportunity?.apy).times(100).toString()
  if (loading || !opportunity) {
    return (
      <Center minW='350px' minH='350px'>
        <CircularProgress isIndeterminate />
      </Center>
    )
  }
  if (foxyBalance.eq(0) && rewardBalance.eq(0)) {
    return (
      <FoxyEmpty
        assets={[stakingAsset, rewardAsset]}
        apy={apy}
        onClick={() =>
          browserHistory.push({
            ...browserLocation,
            pathname: `/defi/${match?.params.earnType}/${match?.params.provider}/deposit/`,
          })
        }
      />
    )
  }
  return (
    <Flex
      width='full'
      minWidth={{ base: '100%', xl: '500px' }}
      maxWidth='fit-content'
      flexDir='column'
    >
      <ModalBody>
        <Stack alignItems='center' justifyContent='center' py={8}>
          <Text color='gray.500' translation='defi.modals.foxyOverview.foxyBalance' />
          <Stack direction='row' alignItems='center' justifyContent='center'>
            <AssetIcon boxSize='10' src={rewardAsset.icon} />
            <Amount.Crypto
              fontSize='3xl'
              fontWeight='medium'
              value={opportunity?.cryptoAmount}
              symbol={rewardAsset?.symbol}
            />
          </Stack>
          <Tag colorScheme='green'>{apy}% APR</Tag>
        </Stack>
      </ModalBody>
      <ModalFooter justifyContent='flex-start' alignItems='flex-start' flexDir='column'>
        <Stack width='full'>
          <Text fontWeight='medium' translation='defi.modals.foxyOverview.withdrawals' />
          <WithdrawCard asset={stakingAsset} {...opportunity.withdrawInfo} />
        </Stack>
      </ModalFooter>
    </Flex>
  )
}
